// src/api/dealFlowList.js
// Cached data for the grouped Deal Flow list: one counts call for every status, and rows per status
// fetched only when that status is opened. Query keys carry the filters, so each filter set caches apart.
import React from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { RetryOnError, verifyToken } from "../utils/VerifyToken";
import { checkForAuthorization } from "utils/index";

const ROOT = "dealflow-list";
// Fresh for 5 min (no refetch on remount/reload); a reload keeps a copy for 30 min, then starts over.
export const DEAL_LIST_STALE_MS = 5 * 60 * 1000;
const PERSIST_MAX_AGE_MS = 30 * 60 * 1000;
// Session storage: survives a reload, not a closed tab, so startup contact data does not linger on the device.
const STORE_PREFIX = "df_list:";

const storeKey = (queryKey) => `${STORE_PREFIX}${JSON.stringify(queryKey)}`;

const readPersisted = (queryKey) => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(storeKey(queryKey)) || "null");
        if (!saved || Date.now() - saved.updatedAt > PERSIST_MAX_AGE_MS) return null;
        return saved;
    } catch {
        return null;
    }
};

// Read once per key, not on every render.
const usePersisted = (queryKey) => {
    const hash = JSON.stringify(queryKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return React.useMemo(() => readPersisted(queryKey), [hash]);
};

const writePersisted = (queryKey, data) => {
    try {
        sessionStorage.setItem(storeKey(queryKey), JSON.stringify({ data, updatedAt: Date.now() }));
    } catch { /* quota or blocked storage: memory cache still works */ }
};

// Drops this investor's stored copies and marks the queries stale; only open groups (and the counts) refetch.
export const invalidateDealFlowList = (queryClient, investorId) => {
    try {
        const prefix = `${STORE_PREFIX}${JSON.stringify([ROOT, investorId]).slice(0, -1)}`;
        Object.keys(sessionStorage).filter((k) => k.startsWith(prefix)).forEach((k) => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
    return queryClient.invalidateQueries([ROOT, investorId]);
};

// --- Optimistic status moves (grouped list) ---
// Every cached copy (any filter scope) is edited in memory and in session storage, so a reload agrees.
const setCached = (queryClient, key, data) => {
    queryClient.setQueryData(key, data);
    writePersisted(key, data);
};

// Status id of the group whose cached rows hold this row (the row's current status).
export const findCachedRowStatus = (queryClient, investorId, isRow) => {
    for (const [key, data] of queryClient.getQueriesData([ROOT, investorId, "rows"])) {
        if (data?.pages?.some((page) => page?.data?.some(isRow))) return key[3];
    }
    return null;
};

// Everything cached for this investor, to put back if the server refuses a move.
export const snapshotDealFlowList = (queryClient, investorId) => queryClient.getQueriesData([ROOT, investorId]);
export const restoreDealFlowList = (queryClient, snapshot = []) => {
    snapshot.forEach(([key, data]) => { if (data !== undefined) setCached(queryClient, key, data); });
};

// Takes the rows out of their old group and shifts the counts; the new group is refreshed after the server agrees.
export const applyOptimisticMove = (queryClient, investorId, { isRow, fromStatus, toStatus, movedCount }) => {
    let removed = 0;
    for (const [key, data] of queryClient.getQueriesData([ROOT, investorId, "rows", fromStatus])) {
        if (!data?.pages) continue;
        let n = 0;
        const pages = data.pages.map((page) => {
            const kept = (page?.data || []).filter((row) => { const hit = isRow(row); if (hit) n += 1; return !hit; });
            return { ...page, data: kept };
        });
        removed = Math.max(removed, n);
        setCached(queryClient, key, { ...data, pages: pages.map((page) => ({ ...page, totalCount: Math.max(0, (page?.totalCount || 0) - n) })) });
    }
    const delta = movedCount ?? removed;
    if (!delta) return;
    // The new group's rows arrive with its refetch; its total moves now so it agrees with the shifted count.
    for (const [key, data] of queryClient.getQueriesData([ROOT, investorId, "rows", toStatus])) {
        if (!data?.pages) continue;
        setCached(queryClient, key, { ...data, pages: data.pages.map((page) => ({ ...page, totalCount: (page?.totalCount || 0) + delta })) });
    }
    for (const [key, data] of queryClient.getQueriesData([ROOT, investorId, "counts"])) {
        if (!data) continue;
        setCached(queryClient, key, {
            ...data,
            [fromStatus]: Math.max(0, (data[fromStatus] || 0) - delta),
            [toStatus]: (data[toStatus] || 0) + delta,
        });
    }
};

// After the server agrees: the counts confirm, and both groups refetch if open (else on next open). The old group
// is refetched too, not trusted from the optimistic edit: other changes (AI shortlist, teammates) may have moved
// more startups out of it meanwhile, and a stale list there disagreed with its fresh count.
export const settleDealFlowMove = (queryClient, investorId, { fromStatus, toStatus }) => {
    try {
        const prefix = `${STORE_PREFIX}${JSON.stringify([ROOT, investorId, "rows", toStatus]).slice(0, -1)}`;
        Object.keys(sessionStorage).filter((k) => k.startsWith(prefix)).forEach((k) => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
    queryClient.invalidateQueries([ROOT, investorId, "rows", toStatus]);
    queryClient.invalidateQueries([ROOT, investorId, "counts"]);
    if (fromStatus) queryClient.invalidateQueries([ROOT, investorId, "rows", fromStatus]);
};

// The list request minus paging: what identifies one result set.
const requestScope = (request) => {
    const { page, limit, ...payload } = request?.payload || {};
    return { payload, queryParams: request?.queryParams || {} };
};

const joinIds = (v) => (Array.isArray(v) ? v.join(",") : v);

// GET /kanban/counts/:investor_id -> { data: { counts: { [statusId]: n } } }
export const useDealFlowStatusCounts = (investorId, request, { enabled = true } = {}) => {
    const scope = requestScope(request);
    const queryKey = [ROOT, investorId, "counts", scope];
    const persisted = usePersisted(queryKey);
    return useQuery(
        queryKey,
        async () => {
            const { payload, queryParams } = scope;
            const res = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/kanban/counts/${investorId}`,
                headers: { Authorization: `Bearer ${checkForAuthorization()}` },
                params: {
                    investor_pipeline_id: payload.investor_pipeline_id,
                    name: payload.name || undefined,
                    category: joinIds(queryParams.category),
                    sub_sector: joinIds(queryParams.sub_sector),
                    select_stages: joinIds(queryParams.select_stages),
                    city: joinIds(queryParams.city),
                },
            });
            return res?.data?.data?.counts || {};
        },
        {
            enabled: Boolean(investorId) && enabled,
            staleTime: DEAL_LIST_STALE_MS,
            cacheTime: PERSIST_MAX_AGE_MS,
            refetchOnWindowFocus: false,
            // A new search/filter keeps the last result on screen (dimmed) until the new one lands.
            keepPreviousData: true,
            initialData: persisted?.data,
            initialDataUpdatedAt: persisted?.updatedAt,
            onSuccess: (data) => writePersisted(queryKey, data),
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};

// Location filter options: the cities of every loaded group in this pipeline (the old table had its tab's cities).
export const useDealFlowCityOptions = (investorId, pipelineId) => {
    const queryClient = useQueryClient();
    const [cities, setCities] = React.useState([]);
    React.useEffect(() => {
        const pipeline = pipelineId && pipelineId !== "All" ? pipelineId : undefined;
        const collect = () => {
            const byCity = new Map();
            for (const [key, data] of queryClient.getQueriesData([ROOT, investorId, "rows"])) {
                if (key[4]?.payload?.investor_pipeline_id !== pipeline) continue;
                (data?.pages?.[0]?.uniqueCountryCity || []).forEach((c) => { if (c?.city_id) byCity.set(String(c.city_id), c); });
            }
            const next = [...byCity.values()].sort((a, b) =>
                `${a.country}|${a.city}`.toLowerCase().localeCompare(`${b.country}|${b.city}`.toLowerCase()));
            setCities((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
        };
        collect();
        // Cache events fire while groups render; batch to the next frame so no state is set mid-render.
        let frame = 0;
        const unsubscribe = queryClient.getQueryCache().subscribe(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(collect);
        });
        return () => { unsubscribe(); cancelAnimationFrame(frame); };
    }, [queryClient, investorId, pipelineId]);
    return cities;
};

// Rows of one status, page by page ("Show more"). Disabled while the group is closed, so nothing is fetched.
export const useDealFlowStatusRows = (investorId, statusId, buildRequest, pageSize, { enabled = true } = {}) => {
    const firstRequest = buildRequest(statusId, 1, pageSize);
    const queryKey = [ROOT, investorId, "rows", statusId, requestScope(firstRequest), pageSize];
    const persisted = usePersisted(queryKey);
    return useInfiniteQuery(
        queryKey,
        async ({ pageParam = 1 }) => {
            const { payload, investor_id, queryParams } = buildRequest(statusId, pageParam, pageSize);
            const res = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BASE_URL}/investors/get-startup-list/${investor_id}`,
                headers: { Authorization: `Bearer ${checkForAuthorization()}` },
                data: { page: 1, limit: pageSize, sort: -1, name: "", ...payload, ...queryParams },
            });
            // Rows and total; page 1 also keeps the status's cities for the filter's Location options.
            return {
                data: res?.data?.data || [],
                totalCount: res?.data?.totalCount || 0,
                ...(pageParam === 1 && { uniqueCountryCity: res?.data?.uniqueCountryCity || [] }),
            };
        },
        {
            enabled: Boolean(investorId) && Boolean(statusId) && enabled,
            getNextPageParam: (lastPage, pages) => {
                const loaded = pages.reduce((n, p) => n + (p?.data?.length || 0), 0);
                return loaded < (lastPage?.totalCount || 0) && lastPage?.data?.length ? pages.length + 1 : undefined;
            },
            staleTime: DEAL_LIST_STALE_MS,
            cacheTime: PERSIST_MAX_AGE_MS,
            refetchOnWindowFocus: false,
            // A new search/filter keeps the last result on screen (dimmed) until the new one lands.
            keepPreviousData: true,
            initialData: persisted?.data,
            initialDataUpdatedAt: persisted?.updatedAt,
            onSuccess: (data) => writePersisted(queryKey, data),
            retry: (failureCount, error) => RetryOnError(failureCount, error),
            onError: (err) => verifyToken(err),
        }
    );
};
